/**
 * Pure display helpers for the social surfaces (post feed + post detail). These
 * FORMAT server-provided values — never a derived score, never a fabricated
 * count. Honesty rules: a missing author_display_name renders the em-dash
 * placeholder (NEVER the raw author_id / uid); a missing or non-numeric score /
 * comment_count renders the em-dash (never NaN); a missing/unparseable timestamp
 * renders nothing rather than a fabricated time.
 */

const EM_DASH = "—";

/** The privacy-safe placeholder for a missing display_name (never the uid). */
export const MISSING = EM_DASH;

/**
 * The author label — the server's display_name, or the em-dash placeholder when
 * it is absent. NEVER falls back to the raw author_id (a uid is not a name and
 * must not leak into visible text).
 *
 * Defense-in-depth (no-UID-in-UI, common-mistakes.md #8): a display_name that
 * EQUALS the author_id is the resolver's own internal id-equals-name fallback —
 * treat it as UNRESOLVED and degrade to the placeholder too, never echo the raw
 * uid. Pass the corresponding author_id at every call site that has one; when it
 * matches the trimmed name, the label is the em-dash. (A backend that already
 * degrades unresolved names to null makes this a guard, not a live fix — but the
 * FE fences it regardless of which resolver feeds it.)
 */
export function authorLabel(
  displayName: string | null | undefined,
  authorId?: string | null,
): string {
  const trimmed = displayName?.trim();
  if (!trimmed) return MISSING;
  // id-equals-name fallback (compare on the trimmed values) → unresolved.
  if (authorId != null && trimmed === authorId.trim()) return MISSING;
  return trimmed;
}

/**
 * A server count (score / comment_count) as a string, verbatim. Returns the
 * em-dash when the value is absent or not a finite number — never "NaN", never
 * a fabricated 0.
 */
export function countLabel(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value)
    ? String(value)
    : EM_DASH;
}

/**
 * A feed ACTION-BAR engagement count (E1, LinkedIn / NextDoor convention) — like
 * `countLabel`, but a REAL server 0 is HIDDEN (returns null → render no digit, the
 * label stands alone) because a zero count is noise until there is at least one
 * engagement. The three cases stay strictly distinct so honesty is preserved:
 *
 *   - value >= 1   → the number verbatim (e.g. "3")
 *   - value === 0  → null  → the caller renders NO count element (label only)
 *   - absent       → the em-dash (null/undefined/non-finite — a missing field is
 *                    NOT a real 0 and must NOT be hidden-as-if-zero; it was never
 *                    "0" and stays the em-dash placeholder)
 *
 * This is ONLY for the feed action bar; the post-DETAIL "0 comments" pluralized
 * line intentionally shows its 0 and uses `countLabel` directly.
 */
export function actionCountLabel(value: number | null | undefined): string | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return EM_DASH;
  if (value === 0) return null;
  return String(value);
}

/**
 * Up-to-two-letter INITIALS for an avatar fallback (PostResponse carries no
 * avatar URL, so the avatar is always an initials chip — never a fabricated
 * image). Derived ONLY from the display_name: the first letter of the first two
 * alphanumeric words, uppercased. Returns "" when there is no usable name (the
 * caller then renders a neutral, text-free placeholder chip rather than echoing
 * the uid or the em-dash a second time). Non-alphanumeric leading characters
 * (e.g. a hostile "<img …>" name) are stripped, so the chip never surfaces raw
 * markup glyphs — React would escape them anyway, but the chip stays clean.
 */
export function authorInitials(displayName: string | null | undefined): string {
  const trimmed = displayName?.trim();
  if (!trimmed) return "";
  const letters = trimmed
    .split(/\s+/)
    .map((word) => {
      const m = word.match(/[\p{L}\p{N}]/u);
      return m ? m[0] : "";
    })
    .filter(Boolean)
    .slice(0, 2)
    .join("");
  return letters.toUpperCase();
}

/**
 * Format a server timestamp (last_message_at) as a SHORT relative "5m" / "3h" /
 * "2d" / "1w" token for the inbox list — a compact recency chip beside each
 * conversation row. This FORMATS a server value into a coarse bucket; it is NOT
 * client-side business logic (no ordering / unread decision rides on it — the
 * list order and unread_count come from the server).
 *
 * Honesty: returns null for a missing/unparseable value so the caller renders NO
 * time chip rather than a fabricated one (never "Invalid Date", never the literal
 * "null"). A future timestamp (clock skew) collapses to "now". Buckets:
 *   < 60s → "now"; < 60m → "{m}m"; < 24h → "{h}h"; < 7d → "{d}d"; else "{w}w".
 */
export function relativeTimeLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;

  const diffMs = Date.now() - ms;
  // Future timestamp (clock skew) → "now" rather than a negative token.
  const secs = Math.max(0, Math.floor(diffMs / 1000));
  if (secs < 60) return "now";

  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;

  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;

  const weeks = Math.floor(days / 7);
  return `${weeks}w`;
}

/** Items at or beyond this age render an absolute date rather than a relative token. */
const RELATIVE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * The UNIFIED social post/comment timestamp (U9). Modern social surfaces show
 * RELATIVE time ("now"/"5m"/"3h"/"2d") for recent items — it is more scannable AND
 * it sidesteps the day-boundary inconsistency the post/comment byline used to have
 * (the post showed a date-only UTC label while comments showed a date+time LOCAL
 * label, so a late-evening post could read a LATER calendar day than its own
 * comments). Used for BOTH the feed card post stamp, the post-detail post byline,
 * and each comment time. This FORMATS a server `created_at` into a coarse bucket /
 * date; it is NOT client-side business logic (no ordering / recency decision rides
 * on it — the server owns sequence and counts).
 *
 *   - < 7 days old  → the RELATIVE token (delegates to relativeTimeLabel — the
 *     bucket logic is NOT duplicated; a future skew there collapses to "now").
 *   - >= 7 days old → an absolute SHORT date "Jun 2, 2026", PINNED to
 *     `timeZone: "UTC"` (#1446) so an old post renders the SAME calendar day for
 *     every reader and never drifts a day early in negative-offset zones.
 *
 * Honesty: a missing OR unparseable value returns null so the caller renders NO
 * time chip — never "Invalid Date", never "NaN", never the literal "null". (This
 * is stricter than postDateLabel, which echoes the raw ISO on an unparseable
 * value; a relative label has no meaningful raw fallback.)
 */
export function socialTimeLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;

  // Recent (and future-skewed) items → the relative bucket. relativeTimeLabel
  // owns the now/m/h/d/w thresholds AND the clock-skew → "now" rule; reuse it
  // rather than re-deriving the buckets here.
  if (Date.now() - ms < RELATIVE_WINDOW_MS) {
    return relativeTimeLabel(value);
  }

  // Older items → an absolute, UTC-pinned short date (same instant→day mapping as
  // postDateLabel, but null — not the raw ISO — on the unparseable path above).
  // The year only when it is not the current one: "Sep 20", "Dec 3, 2025".
  try {
    const date = new Date(ms);
    const sameYear = date.getUTCFullYear() === new Date().getUTCFullYear();
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      ...(sameYear ? {} : { year: "numeric" }),
      timeZone: "UTC",
    }).format(date);
  } catch {
    return null;
  }
}

/**
 * Format a message's server timestamp (created_at) as a TIME-OF-DAY chip
 * "5:00 PM" for a chat bubble — the conversation already establishes the day, so
 * a bubble shows the time only. This FORMATS a server value into a stamp; it
 * makes no ordering / recency decision (the thread's order is the reverse of the
 * server array, and the server is the source of truth for sequence).
 *
 * Honesty: returns null for a missing value (the bubble then renders NO time
 * chip rather than a fabricated one); the raw ISO is the last-resort fallback for
 * an UNPARSEABLE value so a stamp never renders the literal "Invalid Date". Same
 * contract as postDateLabel / relativeTimeLabel.
 */
export function messageTimeLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return value;
  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(ms));
  } catch {
    return value;
  }
}

/**
 * Format a server timestamp as a DATE-ONLY "Jun 2, 2026" — no time of day. Used by
 * the DM thread's day-dividers (the label between two messages on different
 * calendar days). Honesty: null for a missing value, the raw ISO as the last-resort
 * fallback for an unparseable one. (Social POST/comment timestamps now use
 * `socialTimeLabel` — relative, with this same UTC-pinned short date as their
 * >= 7-day fallback.)
 *
 * Pinned to `timeZone: "UTC"` (#1446): a date-only label must render the SAME
 * calendar day for every reader, so the server's UTC instant is formatted in UTC
 * rather than the viewer's local zone — otherwise a UTC-midnight created_at drew
 * a day early in negative-offset zones (the Americas). The instant/relative
 * helpers above intentionally stay local; only this date-only label is UTC-fixed.
 */
export function postDateLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return value;
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(ms));
  } catch {
    return value;
  }
}
